import {useState} from 'react';

import {useToast} from '@kit';

import {useCharacter} from '../character-context';
import {spellHasEffect, toggleActiveSpell} from '../model/active-spells';
import type {CharacterSpell, SpellCasting} from '../model/collect-effects';
import {withJournalEntry} from '../model/journal';
import {
	availableSlots,
	castingRemaining,
	spellCastingStats,
	spellDiceFormula,
	spendCastingPatch,
} from '../model/spell-casting';
import type {SpellSlot} from '../model/spell-slots';

export const slotTitle = (slot: SpellSlot) => `${slot.pact ? 'договор, ' : ''}${slot.level} круг`;

function spentToast(casting: SpellCasting, slot: SpellSlot | undefined, remaining: number) {
	if (casting.kind === 'slot' && slot) {
		const left = slot.max - slot.spent - 1;
		return {title: `Потрачена ячейка ${slot.pact ? 'договора ' : ''}${slot.level} круга, осталось ${left}`};
	}
	const cost = casting.kind === 'resource' ? casting.cost || 1 : 1;
	const left = Number.isFinite(remaining) ? `, осталось ${remaining - 1}` : '';
	return {
		title:
			casting.kind === 'resource'
				? `Потрачено зарядов: ${cost}${left}`
				: `Потрачено использование${casting.source ? ` «${casting.source}»` : ''}${left}`,
	};
}

export function useSpellCasting(spell: CharacterSpell, casting: SpellCasting) {
	const {catalog, character, update} = useCharacter();
	const toast = useToast();
	const data = catalog.getSpell(spell.id);
	const [slotKey, setSlotKey] = useState<string | undefined>();

	const level = data?.level ?? 0;
	const slots = casting.kind === 'slot' && level > 0 ? availableSlots(character, level) : [];
	const slot = slots.find((candidate) => candidate.key === slotKey) || slots[0];
	const stats = spellCastingStats(catalog, character, spell, casting);
	const dice = data && spellDiceFormula(data, slot?.level ?? level, character.level, stats.modifier);
	const isFree = casting.kind === 'slot' && level === 0;
	const canCast = isFree || castingRemaining(character, casting, level) > 0;
	const hasAttack = !!data?.attackType && stats.attackBonus != null;

	const cast = () => {
		if (isFree || !canCast || !data) return;
		update((current) => {
			const isActive = (current.activeSpells || []).some((active) => active.spellId === spell.id);
			const activate = spellHasEffect(catalog, spell.id) && !isActive;
			return {
				...spendCastingPatch(current, casting, slot),
				...(activate ? {activeSpells: toggleActiveSpell(current.activeSpells || [], catalog, spell.id)} : null),
				...withJournalEntry(current, {
					kind: 'spell',
					title: data.title,
					details: casting.kind === 'slot' && slot ? slotTitle(slot) : casting.source,
				}),
			};
		});
		toast.show({
			intent: 'success',
			...spentToast(casting, slot, castingRemaining(character, casting, level)),
			body: data.title,
		});
	};

	return {
		data,
		slots,
		slot,
		setSlotKey,
		stats,
		dice,
		isFree,
		canCast,
		hasAttack,
		cast,
	};
}
